import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextInput,
  Button,
  Title,
  Box,
  Grid,
  Group,
  Textarea,
  Switch,
  Select,
  NumberInput,
  Text,
  Stack,
  UnstyledButton,
  Divider,
  Alert,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
} from '@hello-pangea/dnd';
import {
  IconUser,
  IconShield,
  IconUsers,
  IconWorld,
  IconGripVertical,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useTripByIdQuery,
  useCreateTripMutation,
  useReplaceTripMutation,
  useEnableShareLinkMutation,
  useRevokeShareLinkMutation,
  useRotateShareLinkMutation,
} from '../../hooks/useTripsQueries';
import {
  type TripFormState,
  type Occasion,
  type Visibility,
  type Status,
} from '../../types';
import { useLocale } from '../../contexts/LocaleContext';
import { ErrorDisplay } from '../common/ErrorDisplay';
import {
  type ProcessedError,
  errorHandler,
  isProcessedError,
} from '../../services/utils/errorHandler';
import { validateTripForm } from '../../utils/validation/tripValidation';
import { buildRequest } from '../../utils/trips/buildRequest';
import { buildSummaryFormState } from '../../utils/trips/buildSummaryFormState';
import { deriveStatusWarning } from '../../utils/trips/deriveStatusWarning';
import { resolveDates } from '../../utils/trips/resolveDates';
import {
  OCCASIONS,
  OCCASION_LABELS,
  STATUS_LABELS,
  VISIBILITY_LABELS,
  buildShareUrl,
} from '../../constants';
import {
  OCCASION_COLORS,
  OCCASION_DEFAULT_COLORS,
  STATUS_COLORS,
} from '../../theme/tokens';
import {
  fromMantineDateTimeString,
  toApiLocalDateTime,
} from '../../utils/mantineDateUtils';
/** TODO: Review
 * Use api.frankfurter.dev to retrieve currencies vs hard coding. Need to decide whether to
 * call it on the frontend or the backend and whether currency should be pre-populated
 * on app start up or update (similar to location) whenver user propmpts for currency */
const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD'];

const EMPTY_FORM: TripFormState = {
  name: '',
  description: '',
  visibility: 'CREATOR',
  status: 'PLANNING',
  occasions: [],
  lockEnabled: false,
  lockStartDate: '',
  lockEndDate: '',
  budgetEnabled: false,
  budgetAmount: '',
  budgetCurrency: 'USD',
};

const VISIBILITY_OPTIONS: {
  value: Visibility;
  icon: React.ReactNode;
  description: string;
}[] = [
  {
    value: 'CREATOR',
    icon: <IconUser size={18} />,
    description: 'Only the person who created the trip',
  },
  {
    value: 'ADMINS',
    icon: <IconShield size={18} />,
    description: 'Trip creator and co-admins',
  },
  {
    value: 'MEMBERS',
    icon: <IconUsers size={18} />,
    description: 'Admins and all travelers',
  },
  {
    value: 'PUBLIC',
    icon: <IconWorld size={18} />,
    description: 'Anyone with an account',
  },
];

export interface DetailsFormProps {
  tripId?: string;
}

const DetailsForm: React.FC<DetailsFormProps> = ({ tripId: id }) => {
  const navigate = useNavigate();
  /** TanStack Query: trip by id (only runs when id is set; shared cache if same id used elsewhere). */
  const tripById = useTripByIdQuery(id);
  /** TanStack Query: create trip (on submit, on success; invalidates trips list so TripList refetches).
   * The hooks don't receive trip data. It returns an object whose methods receive it when you call them.
   * So the "parameters" are passed when you invoke those methods, not when you call the hooks, see await
   * updateTripMutation.mutateAsync({ id, tripData: payload });
   */
  const createTripMutation = useCreateTripMutation();
  /** TanStack Query: replace trip (on submit, on success; invalidates trips list so TripList refetches). */
  const replaceTripMutation = useReplaceTripMutation();
  /** TanStack Query: enable share link (on toggle, on success; invalidates trips list so TripList refetches). */
  const enableShareMutation = useEnableShareLinkMutation();
  /** TanStack Query: revoke share link (on toggle, on success; invalidates trips list so TripList refetches). */
  const revokeShareMutation = useRevokeShareLinkMutation();
  /** TanStack Query: rotate share link (on button click, on success; invalidates trips list so TripList refetches). */
  const rotateShareMutation = useRotateShareLinkMutation();
  const [submitError, setSubmitError] = useState<ProcessedError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusWarningDismissed, setStatusWarningDismissed] = useState(false);
  const { locale } = useLocale();

  const [formData, setFormData] = useState<TripFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof TripFormState, string>>
  >({});

  const isEditing = !!id;
  const loading = tripById.isLoading;
  const trip = tripById.data;
  const shareUrl = buildShareUrl(trip?.shareToken);

  useEffect(() => {
    if (!isEditing || !trip) {
      return;
    }
    setFormData(buildSummaryFormState(trip, locale));
  }, [isEditing, trip, locale]); // tells React to run this effect again whenever:
  // - isEditing changes (e.g. from false to true - user goes from create to edit or the other way around)
  // - trip changes (e.g. from null to a trip object - query finishes and tripById.data is set, or the user navigates to a different trip id)
  // - locale changes (e.g. from 'en-US' to 'fr-FR' - so the displayed dates are re-formatted for the new locale)

  /** Map query errors (e.g. 404) to ProcessedError for ErrorDisplay. */
  useEffect(() => {
    if (!tripById.error) {
      setSubmitError(null);
      return;
    }
    setSubmitError(
      isProcessedError(tripById.error)
        ? tripById.error
        : errorHandler.processError(tripById.error as Error)
    );
  }, [tripById.error]);

  const statusWarning = useMemo(() => {
    const lockEndWire = formData.lockEnabled
      ? (() => {
          const parsed = fromMantineDateTimeString(formData.lockEndDate);
          return parsed ? toApiLocalDateTime(parsed) : null;
        })()
      : (trip?.derivedEndDate ?? null);
    const source = formData.lockEnabled
      ? 'locked'
      : trip
        ? resolveDates(trip).source
        : 'none';
    return deriveStatusWarning(formData.status, source, lockEndWire);
  }, [formData, trip]);

  useEffect(() => {
    setStatusWarningDismissed(false);
  }, [
    formData.status,
    formData.lockEnabled,
    formData.lockStartDate,
    formData.lockEndDate,
    trip?.derivedEndDate,
  ]);

  const coverColors = useMemo(() => {
    const primary = formData.occasions[0];
    return (primary && OCCASION_COLORS[primary]) || OCCASION_DEFAULT_COLORS;
  }, [formData.occasions]);

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const { errors, isValid } = validateTripForm(formData);
    if (!isValid) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = buildRequest(formData);

      if (isEditing && id) {
        await replaceTripMutation.mutateAsync({ id, tripData: payload });
        notifications.show({
          title: 'Success',
          message: 'Trip updated successfully!',
          color: 'green',
          autoClose: 1500,
        });
        navigate('/trips');
      } else {
        const result = await createTripMutation.mutateAsync(payload);
        notifications.show({
          title: 'Success',
          message: result?.message ?? 'Trip created successfully!',
          color: 'green',
          autoClose: 1500,
        });
        navigate('/trips');
      }
    } catch (err) {
      setSubmitError(
        isProcessedError(err) ? err : errorHandler.processError(err as Error)
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = <K extends keyof TripFormState>(
    field: K,
    value: TripFormState[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleOccasion = (occasion: Occasion) => {
    setFormData(prev => {
      const exists = prev.occasions.includes(occasion);
      return {
        ...prev,
        occasions: exists
          ? prev.occasions.filter(id => id !== occasion)
          : [...prev.occasions, occasion],
      };
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const items = Array.from(formData.occasions);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setFormData(prev => ({ ...prev, occasions: items }));
  };

  const handleShareToggle = async (enabled: boolean) => {
    if (!id) {
      return;
    }
    try {
      if (enabled) {
        await enableShareMutation.mutateAsync(id);
      } else {
        await revokeShareMutation.mutateAsync(id);
      }
      notifications.show({
        title: enabled ? 'Share enabled' : 'Share revoked',
        message: enabled
          ? 'Anyone with the link can view this trip.'
          : 'Share link has been revoked.',
        color: 'green',
        autoClose: 2000,
      });
    } catch (err) {
      setSubmitError(
        isProcessedError(err) ? err : errorHandler.processError(err as Error)
      );
    }
  };

  const handleRotateShare = async () => {
    if (!id) {
      return;
    }
    try {
      await rotateShareMutation.mutateAsync(id);
      notifications.show({
        title: 'New link generated',
        message: 'Share link has been rotated.',
        color: 'green',
        autoClose: 2000,
      });
    } catch (err) {
      setSubmitError(
        isProcessedError(err) ? err : errorHandler.processError(err as Error)
      );
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareUrl) {
      return;
    }
    await navigator.clipboard.writeText(shareUrl);
    notifications.show({
      title: 'Copied',
      message: 'Share link copied to clipboard.',
      color: 'green',
      autoClose: 1500,
    });
  };

  const busy =
    loading ||
    isSubmitting ||
    createTripMutation.isPending ||
    replaceTripMutation.isPending ||
    enableShareMutation.isPending ||
    revokeShareMutation.isPending ||
    rotateShareMutation.isPending;

  return (
    <Container size="md" mt="xl" mb="xl">
      <Paper shadow="md" p="xl" radius="md">
        <Title order={1} ta="center" mb="lg">
          {isEditing ? 'Edit Trip' : 'Create New Trip'}
        </Title>

        {submitError && <ErrorDisplay error={submitError} className="mb-4" />}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack gap="xl">
            <Stack gap="sm">
              <TextInput
                label="Name"
                placeholder="Enter trip title"
                name="name"
                data-cy="trip-name"
                value={formData.name}
                onChange={e => handleChange('name', e.target.value)}
                error={formErrors.name}
                disabled={busy}
                required
              />
              <Textarea
                label="Description"
                placeholder="Enter trip description"
                rows={4}
                data-cy="trip-description"
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                disabled={busy}
              />
              <Text size="xs" c="dimmed">
                Optional — shown as subtitle on the card.
              </Text>
            </Stack>
            <Divider />
            <Stack gap="sm">
              <Group gap="xs" justify="center">
                <Title order={4} m={0}>
                  Occasions
                </Title>
                <Box
                  w={16}
                  h={16}
                  style={{
                    backgroundColor: coverColors.coverBg,
                    borderRadius: '50%',
                  }}
                  aria-hidden
                />
              </Group>
              <Text size="xs" c="dimmed">
                Select one or more. Drag to set the primary — it controls the
                card color.
              </Text>
              <Group gap="xs" wrap="wrap">
                {OCCASIONS.map(occasionId => {
                  const selected = formData.occasions.includes(occasionId);
                  const colors =
                    OCCASION_COLORS[occasionId] ?? OCCASION_DEFAULT_COLORS;
                  return (
                    <Button
                      key={occasionId}
                      variant={selected ? 'filled' : 'outline'}
                      size="xs"
                      onClick={() => toggleOccasion(occasionId)}
                      disabled={busy}
                      styles={
                        selected
                          ? {
                              root: {
                                backgroundColor: colors.badgeFill,
                                color: colors.badgeText,
                              },
                            }
                          : undefined
                      }
                    >
                      {OCCASION_LABELS[occasionId]}
                    </Button>
                  );
                })}
              </Group>
              {formData.occasions.length > 0 && (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="occasions">
                    {provided => (
                      <Stack
                        gap="xs"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {formData.occasions.map((occasionId, index) => (
                          <Draggable
                            key={occasionId}
                            draggableId={occasionId}
                            index={index}
                          >
                            {(dragProvided, snapshot) => (
                              <Group
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                gap="xs"
                                p="xs"
                                style={{
                                  ...dragProvided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging
                                    ? 'var(--mantine-color-dark-5)'
                                    : 'var(--mantine-color-dark-6)',
                                  borderRadius: 'var(--mantine-radius-sm)',
                                }}
                              >
                                <Box
                                  {...dragProvided.dragHandleProps}
                                  aria-label={`Reorder ${OCCASION_LABELS[occasionId]}`}
                                >
                                  <IconGripVertical size={16} />
                                </Box>
                                <Text size="sm" flex={1}>
                                  {index + 1}. {OCCASION_LABELS[occasionId]}
                                </Text>
                              </Group>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Stack>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Stack>
            <Divider />
            <Stack gap="sm">
              <Title order={4}>Status</Title>
              <Text size="xs" c="dimmed">
                Track where this trip is in its lifecycle.
              </Text>
              <Group justify="center">
                <Select
                  label="Status"
                  data-cy="trip-status"
                  data={Object.entries(STATUS_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                  value={formData.status}
                  onChange={value =>
                    handleChange('status', (value as Status) ?? 'PLANNING')
                  }
                  disabled={busy}
                  w={180}
                  styles={{
                    input: {
                      backgroundColor:
                        STATUS_COLORS[formData.status]?.bg ?? undefined,
                      color: STATUS_COLORS[formData.status]?.text ?? undefined,
                    },
                  }}
                />
              </Group>
              <Text size="xs" c="dimmed">
                Status is user-controlled — the system will never change it
                automatically.
              </Text>
            </Stack>
            <Divider />
            <Stack gap="sm">
              <Title order={4}>Budget</Title>
              <Text size="xs" c="dimmed">
                A reference number — not tracked per stop.
              </Text>
              <Switch
                label="Set a budget"
                checked={formData.budgetEnabled}
                onChange={e =>
                  handleChange('budgetEnabled', e.currentTarget.checked)
                }
                disabled={busy}
              />
              {formData.budgetEnabled && (
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <NumberInput
                      label="Amount"
                      value={formData.budgetAmount}
                      onChange={value =>
                        handleChange('budgetAmount', String(value ?? ''))
                      }
                      min={0}
                      decimalScale={2}
                      error={formErrors.budgetAmount}
                      disabled={busy}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label="Currency"
                      data={CURRENCY_OPTIONS}
                      value={formData.budgetCurrency}
                      onChange={value =>
                        handleChange('budgetCurrency', value ?? 'USD')
                      }
                      error={formErrors.budgetCurrency}
                      disabled={busy}
                    />
                  </Grid.Col>
                </Grid>
              )}
            </Stack>
            <Divider />
            <Stack gap="sm">
              <Title order={4}>Dates</Title>
              <Text size="xs" c="dimmed">
                By default, trip dates are derived from your itinerary stops.
              </Text>
              <Alert variant="light" color="blue">
                Lock specific dates if you want to set the range itinerary. The
                lock badge appears on your card.
              </Alert>
              <Switch
                label="Lock dates"
                checked={formData.lockEnabled}
                onChange={e =>
                  handleChange('lockEnabled', e.currentTarget.checked)
                }
                disabled={busy}
              />
              <Text size="xs" c="dimmed">
                Override itinerary-derived range
              </Text>
              {formData.lockEnabled && (
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <DateTimePicker
                      label="Lock start"
                      value={formData.lockStartDate}
                      onChange={value =>
                        handleChange('lockStartDate', value ?? '')
                      }
                      disabled={busy}
                      error={formErrors.lockStartDate}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <DateTimePicker
                      label="Lock end"
                      value={formData.lockEndDate}
                      onChange={value =>
                        handleChange('lockEndDate', value ?? '')
                      }
                      disabled={busy}
                      error={formErrors.lockEndDate}
                    />
                  </Grid.Col>
                </Grid>
              )}
              {statusWarning && !statusWarningDismissed && (
                <Alert color="yellow" title="Status may be outdated">
                  <Text size="sm" mb="sm">
                    Current status is{' '}
                    {STATUS_LABELS[statusWarning.currentStatus]}; consider
                    changing to {STATUS_LABELS[statusWarning.suggestedStatus]}.
                  </Text>
                  <Group>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() =>
                        handleChange('status', statusWarning.suggestedStatus)
                      }
                    >
                      Update to {STATUS_LABELS[statusWarning.suggestedStatus]}
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => setStatusWarningDismissed(true)}
                    >
                      Keep {STATUS_LABELS[statusWarning.currentStatus]}
                    </Button>
                  </Group>
                </Alert>
              )}
            </Stack>
            <Divider />
            <Stack gap="sm">
              <Title order={4} id="visibility-heading">
                Visibility
              </Title>
              <Text size="xs" c="dimmed">
                Control who can see this trip.
              </Text>
              <Stack
                gap="xs"
                role="radiogroup"
                aria-labelledby="visibility-heading"
              >
                {VISIBILITY_OPTIONS.map(option => (
                  <UnstyledButton
                    key={option.value}
                    role="radio"
                    aria-checked={formData.visibility === option.value}
                    tabIndex={formData.visibility === option.value ? 0 : -1}
                    data-cy={`trip-visibility-${option.value}`}
                    onClick={() => handleChange('visibility', option.value)}
                    disabled={busy}
                    p="sm"
                    style={{
                      borderRadius: 'var(--mantine-radius-sm)',
                      border:
                        formData.visibility === option.value
                          ? '2px solid var(--mantine-color-horizon-5)'
                          : '1px solid var(--mantine-color-dark-4)',
                    }}
                  >
                    <Group>
                      {option.icon}
                      <Box ta="left">
                        <Text size="sm" fw={500}>
                          {VISIBILITY_LABELS[option.value]}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {option.description}
                        </Text>
                      </Box>
                    </Group>
                  </UnstyledButton>
                ))}
              </Stack>
            </Stack>

            {isEditing && trip && (
              <Stack gap="sm">
                <Title order={4}>Share link</Title>
                <Text size="xs" c="dimmed">
                  Generate a read-only link independent of visibility. Revoke
                  without generating a new one. You can also rotate the link to
                  generate a new one.
                </Text>
                <Switch
                  label="Enable share link"
                  checked={trip.shareLinkEnabled}
                  onChange={e => handleShareToggle(e.currentTarget.checked)}
                  disabled={busy}
                />
                <Text size="xs" c="dimmed">
                  Anyone with the link can view the itinerary.
                </Text>
                {trip.shareLinkEnabled && shareUrl && (
                  <>
                    <Group gap="xs">
                      <TextInput
                        label="Share link"
                        flex={1}
                        value={shareUrl}
                        readOnly
                      />
                      <Button
                        variant="outline"
                        onClick={handleCopyShareLink}
                        data-cy="trip-share-copy-form"
                      >
                        Copy
                      </Button>
                    </Group>
                    <Group>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRotateShare}
                        disabled={busy}
                      >
                        Generate new link
                      </Button>
                      <Button
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => handleShareToggle(false)}
                        disabled={busy}
                      >
                        Revoke link
                      </Button>
                    </Group>
                  </>
                )}
              </Stack>
            )}

            <Divider />

            <Group justify="flex-end">
              <Button
                variant="outline"
                color="gray"
                onClick={() => navigate('/trips')}
                disabled={busy}
                data-cy="trip-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="filled"
                loading={busy}
                disabled={busy}
                data-cy="trip-submit"
              >
                {isEditing ? 'Update Trip' : 'Create Trip'}
              </Button>
            </Group>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default DetailsForm;
